import React from 'react';
import { styled } from '@this/constants/themes';
import moment from 'moment';
import OrginalBackdrop from '@material-ui/core/Backdrop';
import OriginalCircularProgress from '@material-ui/core/CircularProgress';
import Box from '@material-ui/core/Box';
import Typography from '@material-ui/core/Typography';
import Button from '@material-ui/core/Button';
import InputLabel from '@material-ui/core/InputLabel';
import TextField from '@material-ui/core/TextField';
import type { Color as AlertSeverity } from '@material-ui/lab/Alert';
import type { DateFormatFunction, DateRangeFormatFunction } from 'react-big-calendar';
import { Cancel as CancelIcon } from '@material-ui/icons';
import muiTheme from '@this/components/appoint/mui_theme';
import type Adjustment from '@this/domain/appoint/adjustment';

export const H2 = styled(Typography).attrs(() => ({ variant: 'h2', color: 'textPrimary' }))`
  margin-bottom: 20px;
  font-size: 24px;
  font-weight: 500;
  line-height: 32px;
`;

export const H3 = styled(Typography).attrs(() => ({ variant: 'h3', color: 'textPrimary' }))`
  margin-bottom: 20px;
  font-size: 18px;
  font-weight: 500;
  line-height: 24px;
`;

export type SnackbarContent = {
  message: string;
  severity: AlertSeverity;
};

export const Wrapper = styled.div`
  display: flex;
`;

export const Left = styled.div`
  width: 300px;
  margin-right: 20px;
  font-size: 14px;
  flex-shrink: 0;
`;

export const Right = styled.div`
  flex-grow: 99;
`;

export const Section = styled(Box)`
  margin-bottom: 30px;
`;

export const Backdrop = styled(OrginalBackdrop)`
  z-index: 1200;
`;

export const CircularProgress = styled(OriginalCircularProgress)`
  color: white;
`;

export const LoadingBackdrop = ({ open }: { open: boolean }) => {
  return (
    <Backdrop open={open}>
      <CircularProgress />
    </Backdrop>
  );
};

export const FlatButton = styled(Button)`
  box-shadow: 0 0;
`;

export const Label = styled(InputLabel)`
  margin-bottom: 8px;
`;

export const LabelText = styled.span`
  margin-right: 8px;
  color: ${props => props.theme.textColor};
  font-size: 16px;
  font-weight: 500;
  line-height: 22px;
  vertical-align: middle;
`;

export const LabelNecessity = styled.span`
  padding: 1px 10px 0;
  border: solid 1px #ef3c15;
  border-radius: 2px;
  color: #ef3c15;
  font-size: 12px;
  font-weight: 500;
  line-height: 18px;
`;

export const FilledTextField = styled(TextField).attrs({ variant: 'filled' })`
  width: 100%;
  margin-bottom: 33px;

  input {
    padding: 17px 12px 10px;
    font-size: 16px;
  }
`;

export const ButtonWrapper = styled.div`
  position: relative;
  width: fit-content;
`;

export const ButtonProgress = styled(OriginalCircularProgress).attrs({ color: 'primary', size: 24 })`
  position: absolute;
  top: 50%;
  left: 50%;
  margin-top: -12px;
  margin-left: -12px;
`;

export const CalendarWrapper = styled.div<{ isMobile: boolean; withFixedButton?: boolean }>`
  position: relative;
  height: calc(100vh - 54px - 12px - 24px);

  .rbc-toolbar {
    width: calc(100% - 44px);
    height: 54px;
    margin: -66px 0 22px;
    color: #606060;

    .rbc-toolbar-label {
      flex-grow: 0;
      margin: 0 auto 0 30px;
    }

    button {
      background: #fff;
      cursor: pointer;

      &.rbc-active {
        background: ${muiTheme.palette.primary.main};
        color: #fff;
      }
    }

    span:first-child {
      background: transparent;

      button {
        &:first-child {
          margin-right: 10px;
          background: #fff;
          border-radius: 4px;
          border-color: #ddd;
          border-width: 1px;
          letter-spacing: 0.05rem;
          font-family: noto sans japanese;
          width: 48px;
          padding-left: 11.5px;
          padding-top: 3px;
          font-size: 11px;
        }

        &:first-child:hover {
          border-color: #243565;
          border-width: 1.5px;
        }

        + button {
          transform: scale(0.6, 1);
          border-radius: 4px;
          border-color: #ddd;
          border-width: 1px;
          width: 48px;
          margin: -6.5px;
          background: #fff;
        }

        + button:hover {
          border-color: #243565;
          border-width: 1.5px;
        }
      }
    }
  }

  .rbc-time-view {
    border: 0;
  }

  .rbc-time-header {
    position: relative;
    margin-right: 0 !important;
    border: 0;

    &:before {
      content: 'JST';
      color: #909090;
      font-size: 0.8rem;
      position: absolute;
      top: 12px;
      left: 4px;
    }

    .rbc-time-header-content {
      border-left: 0;

      .rbc-time-header-cell {
        min-height: 55px;
      }

      .rbc-allday-cell {
        box-sizing: border-box;
        overflow-y: scroll;
      }

      .rbc-row-bg .rbc-day-bg {
        &.holiday,
        &:nth-child(n + 6) {
          background: #f9f9f9;
        }
      }
    }

    .rbc-header {
      position: relative;
      padding: 15px 3px 35px;

      &:before {
        display: block;
        position: absolute;
        top: 0;
        left: calc(50% - 5px);
        width: 10px;
        color: #606060;
        font-size: 10px;
        line-height: 1;
      }

      &:first-child:before {
        content: '月';
      }

      &:nth-child(2):before {
        content: '火';
      }

      &:nth-child(3):before {
        content: '水';
      }

      &:nth-child(4):before {
        content: '木';
      }

      &:nth-child(5):before {
        content: '金';
      }

      &:nth-child(6):before {
        content: '土';
      }

      &:last-child:before {
        content: '日';
      }

      a {
        display: inline-block;
        width: 33.5px;
        padding: 6px 5px;
        border-radius: 50%;
        color: #606060;
        font-size: 20px;
        line-height: 1;
      }

      &.rbc-today a {
        background: ${muiTheme.palette.primary.main};
        color: #fff;
      }

      &.rbc-today:before {
        color: #243565;
      }

      + .rbc-header {
        border-left: 0;
      }
    }

    .rbc-allday-cell {
      border-left: solid 1px #ededed;
      border-right: solid 1px #ededed;
    }
  }

  .rbc-today {
    background: #fff;
  }

  .rbc-time-content {
    box-sizing: border-box;
    border-right: solid 1px #ededed;

    .rbc-time-gutter {
      .rbc-timeslot-group {
        border-bottom: 0;
        color: #909090;

        &:first-child {
          opacity: 0;
        }

        .rbc-time-slot:first-child {
          margin-top: -0.6rem;
          transform: scale(0.8, 0.8);
        }
      }
    }

    .rbc-time-column .rbc-timeslot-group {
      min-height: 50px;
      color: #606060;
      font-size: 0.8rem;
    }

    .rbc-day-slot {
      min-width: auto;

      &.holiday,
      &:nth-child(n + 7) {
        background: #f9f9f9;
      }

      .rbc-event {
        min-height: 33px;
        flex-direction: column-reverse;
        justify-content: flex-end;
        padding: 2px;

        .rbc-addons-dnd-resize-ns-anchor:first-child {
          z-index: 1;
        }

        &.adjustment-candidate-event {
          pointer-events: none;
        }

        &.short_event .rbc-event-label {
          display: none;
        }

        .rbc-event-label {
          margin-bottom: auto;
          font-size: 10px;
          opacity: 0.7;
        }

        .rbc-event-content {
          flex: none;
          overflow: hidden;
          height: auto;
          max-height: calc(100% - 14px);
          min-height: 15px;
          line-height: 1.3;
          font-size: 10px;
        }
      }
    }

    .rbc-current-time-indicator {
      height: 4px;
      background-color: #bf0000;
      border: solid 1px #fff;

      &:before {
        display: block;
        content: '';
        position: absolute;
        top: -4px;
        left: -5px;
        width: 10px;
        height: 10px;
        background-color: #bf0000;
        border-radius: 50%;
        border: solid 1px #bf0000;
      }
    }
  }

  height: calc(100vh - 54px - 12px - 24px ${props => (props.isMobile && props.withFixedButton ? ' - 56px' : '')});

  ${props =>
    props.isMobile &&
    `
    .rbc-toolbar {
      width: 100%;
      margin: 0 0 12px;

      span:first-child button {
        &:first-child {
          width: auto;
          margin-right: 0;
          padding: .275rem .5rem;
        }

        + button {
          position: absolute;
          top: 19px;
        }

        &:nth-child(2) {
          left: 60px;
        }

        &:last-child {
          right: 108px;
        }
      }

      button {
        padding: .275rem .8rem;
        font-size: 11px;
      }

      .rbc-toolbar-label {
        margin-left: auto;
        font-size: 11px;
      }
    }

    .rbc-day-slot {
      .rbc-event-content {
        white-space: no-wrap;
      }
    }
  `}
`;

export const CalendarLoading = styled(OriginalCircularProgress).attrs({ size: 24 })`
  position: absolute;
  top: -50px;
  left: 130px;
`;

export const DrawerIn = styled(Box)`
  padding: 20px 0;
  box-sizing: border-box;
`;

export const drawerSidePadding = '20px';

export const DrawerTitle = styled(Box)`
  display: flex;
  padding: 0 ${drawerSidePadding};
  font-size: 16px;
  font-weight: bold;
  margin-bottom: 20px;
`;

export const DrawerBody = styled(Box)`
  width: 100%;
`;

export const CloseButton = styled(CancelIcon)`
  display: block;
  margin-left: auto;
  color: #ddd;
  cursor: pointer;
`;

const dayHeaderFormat: DateFormatFunction = date => {
  return moment(date).format('YYYY年MM月DD日 (ddd)');
};

const dayRangeHeaderFormat: DateRangeFormatFunction = ({ start, end }) => {
  const s = moment(start);
  const e = moment(end);
  return `${s.format('YYYY年MM月DD日')} 〜 ${e.format('YYYY年MM月DD日')}`;
};

const timeGutterFormat: DateFormatFunction = date => {
  return moment(date).format('HH:00');
};

const dayFormat: DateFormatFunction = date => {
  return moment(date).format('D');
};

export const formats = {
  dayHeaderFormat,
  dayRangeHeaderFormat,
  timeGutterFormat,
  dayFormat
};

export const HiddenArea = styled.div`
  position: absolute;
  left: -9999px;
`;

export type Plan = 'free' | 'pro';

export type AdjustmentFinishedType = {
  adjusting: boolean;
  fixed: boolean;
  cancelled: boolean;
};

export type AdjustmentsByStatusType = {
  adjusting: Adjustment[];
  fixed: Adjustment[];
  cancelled: Adjustment[];
};

export const messages = {
  today: '今日',
  previous: '＜',
  next: '＞',
  day: '日',
  week: '週'
};
